import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getCompanyProfile } from "../api/companies";
import { useAuth } from "../context/AuthContext";
import api from "../api/client";
import Loader from "../components/ui/Loader";
import SEOHead from "../components/SEO/SEOHead";
import JobCard from "../components/ui/ui/JobCard";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { getFullImageUrl } from "../utils/imageHandler";
import { glassCard, glassPanel } from "../utils/glassStyles";
import { isJobExpired } from "../utils/jobSlug";
import {
  ArrowLeft,
  Building2,
  MapPin,
  Globe,
  Briefcase,
  ExternalLink,
  Pencil,
  Info,
  Clock,
  Filter,
} from "lucide-react";

const JOB_FILTERS = [
  { id: "all", label: "All jobs" },
  { id: "active", label: "Active" },
  { id: "recent", label: "Last 24 hours" },
  { id: "week", label: "This week" },
  { id: "expired", label: "Expired" },
];

function filterJobs(jobs, filterId) {
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;

  switch (filterId) {
    case "active":
      return jobs.filter((j) => !isJobExpired(j));
    case "recent":
      return jobs.filter((j) => now - new Date(j.createdAt).getTime() <= day);
    case "week":
      return jobs.filter((j) => now - new Date(j.createdAt).getTime() <= 7 * day);
    case "expired":
      return jobs.filter((j) => isJobExpired(j));
    default:
      return jobs;
  }
}

export default function CompanyProfile() {
  const { recruiterId } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [jobFilter, setJobFilter] = useState("all");
  const [ownProfileId, setOwnProfileId] = useState(null);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setLoading(true);
        const res = await getCompanyProfile(recruiterId);
        setCompany(res.data);
        setError("");
      } catch (err) {
        console.error(err);
        setError("Company profile not found");
        setCompany(null);
      } finally {
        setLoading(false);
      }
    };
    if (recruiterId) fetchCompany();
  }, [recruiterId]);

  useEffect(() => {
    const fetchOwnProfile = async () => {
      if (role === "RECRUITER" && user) {
        try {
          const res = await api.get("/recruiter/profile");
          setOwnProfileId(res.data?.id || null);
        } catch {
          setOwnProfileId(null);
        }
      }
    };
    fetchOwnProfile();
  }, [role, user]);

  const isOwner = ownProfileId && ownProfileId === recruiterId;
  const allJobs = company?.jobs || [];
  const filteredJobs = useMemo(
    () => filterJobs(allJobs, jobFilter),
    [allJobs, jobFilter]
  );
  const activeJobs = allJobs.filter((j) => !isJobExpired(j));

  if (loading) return <Loader />;

  if (error || !company) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4">
        <div className={`${glassPanel} max-w-md w-full text-center`}>
          <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-lg font-semibold mb-4">{error || "Company not found"}</p>
          <Button onClick={() => navigate("/")} variant="outline" className="backdrop-blur-sm">
            Browse Jobs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent relative">
      <SEOHead
        title={`${company.companyName} | Company Profile | Shebeka`}
        description={company.about?.slice(0, 160) || `${company.companyName} job openings on Shebeka`}
        url={`/companies/${recruiterId}`}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="group text-foreground/70 hover:text-foreground hover:bg-white/15 backdrop-blur-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back
          </Button>
          {isOwner && (
            <Button asChild className="gap-2 backdrop-blur-sm">
              <Link to="/recruiter/profile/edit">
                <Pencil className="w-4 h-4" />
                Edit company page
              </Link>
            </Button>
          )}
        </div>

        {/* Info note */}
        <div className={`${glassPanel} mb-6 flex gap-3 items-start text-sm`}>
          <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <p className="text-muted-foreground leading-relaxed">
            Browse open roles from <strong className="text-foreground">{company.companyName}</strong>.
            Use the filters below to find active listings, recent posts, or expired jobs.
            {isOwner && " You can edit your company description and public note from the edit button above."}
          </p>
        </div>

        {/* Company header */}
        <div className={`${glassCard} rounded-[2rem] p-6 sm:p-10 mb-8`}>
          <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-center md:items-start">
            <Avatar className="w-24 h-24 rounded-2xl border-2 border-white/30 bg-white/20 shadow-xl overflow-hidden backdrop-blur-sm shrink-0">
              {company.logoUrl && (
                <img
                  src={getFullImageUrl(company.logoUrl)}
                  alt={company.companyName}
                  className="w-full h-full object-contain p-2"
                />
              )}
              <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white font-black text-3xl uppercase">
                {company.companyName?.[0] || "C"}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1 text-center md:text-left space-y-4">
              <div>
                <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">
                  {company.companyName}
                </h1>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 text-sm text-muted-foreground">
                  {company.location && (
                    <span className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4" /> {company.location}
                    </span>
                  )}
                  {company.website && (
                    <a
                      href={company.website.startsWith("http") ? company.website : `https://${company.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-primary dark:text-accent hover:underline"
                    >
                      <Globe className="w-4 h-4" /> Website
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap justify-center md:justify-start gap-2">
                <Badge className="bg-primary/15 text-primary dark:text-primary/80 border-primary/20 backdrop-blur-sm">
                  <Briefcase className="w-3 h-3 mr-1" />
                  {allJobs.length} job{allJobs.length !== 1 ? "s" : ""} posted
                </Badge>
                {activeJobs.length > 0 && (
                  <Badge className="bg-primary/15 text-primary dark:text-accent border-accent/20 backdrop-blur-sm">
                    {activeJobs.length} active
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {company.about && (
            <div className="mt-8 pt-8 border-t border-white/15 dark:border-white/10">
              <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                About the company
              </h2>
              <p className="text-[15px] leading-7 text-foreground/85 whitespace-pre-wrap">
                {company.about}
              </p>
            </div>
          )}

          {company.publicNote && (
            <div className="mt-6 p-5 rounded-2xl border border-primary/20 bg-primary/10 backdrop-blur-sm">
              <h3 className="text-sm font-bold uppercase tracking-wider text-primary dark:text-accent mb-2 flex items-center gap-2">
                <Info className="w-4 h-4" /> Note from {company.companyName}
              </h3>
              <p className="text-[15px] leading-7 text-foreground/85 whitespace-pre-wrap">
                {company.publicNote}
              </p>
            </div>
          )}
        </div>

        {/* Jobs list with filters */}
        <div className="space-y-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-2xl sm:text-3xl font-bold">
              Jobs at {company.companyName}
            </h2>
            <p className="text-sm text-muted-foreground flex items-center gap-1.5">
              <Filter className="w-4 h-4" />
              Showing {filteredJobs.length} of {allJobs.length}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {JOB_FILTERS.map((f) => (
              <Button
                key={f.id}
                size="sm"
                variant={jobFilter === f.id ? "default" : "outline"}
                onClick={() => setJobFilter(f.id)}
                className={`rounded-full backdrop-blur-sm ${
                  jobFilter !== f.id ? "bg-white/10 dark:bg-white/5 border-white/20" : ""
                }`}
              >
                {f.id === "recent" && <Clock className="w-3.5 h-3.5 mr-1" />}
                {f.label}
              </Button>
            ))}
          </div>

          {allJobs.length === 0 ? (
            <div className={`${glassPanel} text-center py-12`}>
              <Briefcase className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">No job openings at the moment.</p>
              <Button asChild variant="outline" className="mt-4 backdrop-blur-sm">
                <Link to="/">Browse other jobs</Link>
              </Button>
            </div>
          ) : filteredJobs.length === 0 ? (
            <div className={`${glassPanel} text-center py-12`}>
              <p className="text-muted-foreground mb-4">No jobs match this filter.</p>
              <Button variant="outline" onClick={() => setJobFilter("all")} className="backdrop-blur-sm">
                Show all jobs
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
              {filteredJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={{
                    ...job,
                    companyName: company.companyName,
                    companyLogo: company.logoUrl,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
